import React from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { ChevronRight, Home } from 'lucide-react';
import { useHelpdeskNavigation } from '../hooks/useHelpdeskNavigation';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

const routeToBreadcrumb: Record<string, string> = {
  dashboard: 'Dashboard',
  tickets: 'Tickets',
  customers: 'Customers',
  'knowledge-base': 'Knowledge Base',
  settings: 'Settings',
  'org-settings': 'Organization Settings',
  notifications: 'Notifications',
  profile: 'Profile',
};

export const Breadcrumb: React.FC = () => {
  const location = useLocation();
  const { getPath } = useHelpdeskNavigation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Skip breadcrumbs if not on a helpdesk route
  if (!location.pathname.includes('/helpdesk')) {
    return null;
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', href: getPath.dashboard() },
  ];

  // Find helpdesk index in path segments
  const helpdeskIndex = pathSegments.findIndex(segment => segment === 'helpdesk');
  if (helpdeskIndex === -1) return null;

  // Process segments after /helpdesk
  const helpdeskSegments = pathSegments.slice(helpdeskIndex + 1);
  
  helpdeskSegments.forEach((segment, index) => {
    let label = routeToBreadcrumb[segment] || segment;
    let href: string | undefined;
    
    // Handle context-aware labels and paths
    if (segment === 'new') {
      // Check parent segment to determine context
      if (index > 0 && helpdeskSegments[index - 1] === 'tickets') {
        label = 'New Ticket';
        href = getPath.newTicket();
      } else if (index > 0 && helpdeskSegments[index - 1] === 'customers') {
        label = 'New Customer';
        href = getPath.newCustomer();
      } else if (index > 0 && helpdeskSegments[index - 1] === 'knowledge-base') {
        label = 'New Article';
        href = getPath.newArticle();
      }
    } else {
      // Generate href based on segment type
      switch (segment) {
        case 'tickets':
          href = getPath.tickets();
          break;
        case 'customers':
          href = getPath.customers();
          break;
        case 'knowledge-base':
          href = getPath.knowledgeBase();
          break;
        case 'settings':
          href = getPath.settings();
          break;
        case 'org-settings':
          href = getPath.orgSettings();
          break;
        default:
          // For dynamic segments (IDs), don't add href
          href = undefined;
      }
    }
    
    // Don't add link for the last item (current page)
    if (index === helpdeskSegments.length - 1) {
      breadcrumbs.push({ label });
    } else if (href) {
      breadcrumbs.push({ label, href });
    } else {
      breadcrumbs.push({ label });
    }
  });

  // Don't show breadcrumb if we're on the dashboard
  if (helpdeskSegments.length === 1 && helpdeskSegments[0] === 'dashboard') {
    return null;
  }

  return (
    <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-4 overflow-x-auto">
      <div className="flex items-center min-w-max">
        {breadcrumbs.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <ChevronRight className="h-4 w-4 mx-1 flex-shrink-0" />
            )}
            {item.href ? (
              <Link
                to={item.href}
                className="hover:text-gray-900 dark:hover:text-gray-100 transition-colors flex items-center whitespace-nowrap"
              >
                {index === 0 && <Home className="h-4 w-4 mr-1 flex-shrink-0" />}
                <span className="max-w-[200px] truncate">{item.label}</span>
              </Link>
            ) : (
              <span className="text-gray-900 dark:text-gray-100 font-medium flex items-center whitespace-nowrap">
                <span className="max-w-[200px] truncate">{item.label}</span>
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </nav>
  );
};